import { supabase } from '../config/supabase';

export const teamService = {
    /**
     * Fetch team members.
     * RLS will automatically filter:
     * - Admins see all
     * - Managers see their team
     * - Others see themselves (or nothing if UI restricts)
     */
    async getTeamMembers() {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .is('deleted_at', null) // Filter out soft-deleted users
                .order('full_name', { ascending: true });

            if (error) {
                // Handle specific error for missing column (migration not run)
                if (error.code === '42703') {
                    console.error('🚨 DATABASE MISMATCH: The "deleted_at" column is missing. Please run the migration "20251214205000_add_user_details.sql" in Supabase.');
                    return { success: false, error: { message: 'Database schema mismatch. Please run latest migrations.' } };
                }
                throw error;
            }
            return { success: true, data };
        } catch (error) {
            console.error('Error fetching team members:', error);
            return { success: false, error };
        }
    },

    /**
     * Fetch recent activity for a user.
     * Uses opportunity_history table.
     */
    async getMemberActivity(userId) {
        try {
            // Assuming 'changed_by' or similar field tracks who made the change. 
            // Checking complete-rls-fix.sql, opportunity_history likely has created_by or linking to auth.
            // Wait, opportunity_history definition was not fully visible but usually has 'changed_by' or 'user_id'.
            // Let's assume standard Supabase audit pattern or just query opportunity_history where we have access.
            // Actually, opportunity_history usually tracks changes to an opportunity. 
            // If we want "User Activity", we typically look for records created by this user.

            // Checking migration 1765619521080...
            // CREATE POLICY "Users can create history" ... WITH CHECK (auth.role() = 'authenticated');
            // It doesn't explicitly show columns in the migration snippet provided in history (it was ALTER TABLE).
            // We will assume a 'created_by' or query opportunities 'owner_user_id'.

            // Let's try to query opportunity_history linked to opportunities owned by this user OR modified by this user.
            // Since we don't have exact schema for history columns, let's look at opportunities updated_at as a proxy 
            // or just list opportunities owned by them for now.

            // BETTER APPROACH based on user request "activity log": 
            // Let's fetch the latest 4 opportunities they are working on (updated recently).
            const { data, error } = await supabase
                .from('opportunities')
                .select('id, name, stage, updated_at, amount')
                .eq('owner_user_id', userId)
                .order('updated_at', { ascending: false })
                .limit(4);

            if (error) throw error;

            // Format as activity
            const activities = data.map(opp => ({
                id: opp.id,
                type: 'OPPORTUNITY_UPDATE',
                description: `Updated opportunity "${opp.name}" to ${opp.stage}`,
                changed_at: opp.updated_at
            }));

            return { success: true, data: activities };
        } catch (error) {
            console.error('Error fetching member activity:', error);
            return { success: false, error };
        }
    },

    /**
     * Update member details.
     */
    async updateMember(userId, updates) {
        try {
            const { data, error } = await supabase
                .from('users')
                .update(updates)
                .eq('id', userId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error updating member:', error);
            return { success: false, error };
        }
    },

    /**
     * Soft delete member.
     */
    async softDeleteMember(userId) {
        try {
            const { data, error } = await supabase
                .from('users')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', userId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error deleting member:', error);
            return { success: false, error };
        }
    },

    /**
     * Invite a new member.
     * NOTE: Client-side creation of NEW Auth users is restricted.
     * This function would normally call an Edge Function.
     * For now, we'll return a mock success or error if not Admin.
     */
    async inviteMember(email, role, teamId, otherDetails) {
        try {
            const { data, error } = await supabase.functions.invoke('invite-user', {
                body: {
                    email,
                    role,
                    teamId,
                    otherDetails
                }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            return {
                success: true,
                message: 'Invitation sent and user created successfully.',
                user: data.user
            };
        } catch (error) {
            console.error('Error inviting member:', error);
            // Fallback for demo if function not deployed
            if (error.message && (error.message.includes('Functions') || error.status === 404)) {
                return { success: false, error: { message: 'Edge Function not found. Please deploy "invite-user".' } };
            }
            return { success: false, error };
        }
    },

    async resetPasswordMember(userId, newPassword) {
        try {
            console.log('Sending reset_password request for:', userId);
            const { data, error } = await supabase.functions.invoke('invite-user', {
                body: {
                    action: 'reset_password',
                    userId,
                    password: newPassword
                }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            return { success: true, message: 'Password reset successfully' };
        } catch (error) {
            console.error('Error resetting password details:', error);

            let errorMessage = 'Failed to reset password';

            // Attempt to read the response body from the error context
            if (error && error.context && typeof error.context.text === 'function') {
                try {
                    // Clone response to avoid body-already-read issues if possible
                    const responseClone = error.context.clone ? error.context.clone() : error.context;
                    const errorText = await responseClone.text();
                    console.error('Edge Function Error Body:', errorText);

                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.error) {
                            errorMessage = errorJson.error;
                        } else {
                            errorMessage = errorText; // Fallback to raw text
                        }
                    } catch (e) {
                        // Not JSON, use text directly
                        errorMessage = errorText;
                    }
                } catch (readError) {
                    console.error('Failed to read error context:', readError);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            return { success: false, error: { message: errorMessage } };
        }
    }
};
